/* ========== COLUMN CHARTS (vertical bars) ==========
 * Subtypes:
 *  - Clustered Column (drawColumnChart)
 *  - Stacked Column (drawStackedColumnChart, isPercent=false)
 *  - 100% Stacked Column (drawStackedColumnChart, isPercent=true)
 *  - 3-D Column (drawColumnChart with is3D=true; also applies to stacked)
 * =================================================== */

import {
  COLORS,
  drawAxes,
  drawGridlines,
  drawLegend,
  drawCategoryLabels,
  getMinMaxRange,
  shadeColor,
} from './utils';

export function drawColumnChart(ctx, x, y, width, height, chartObj, is3D) {
  var seriesArr = chartObj && chartObj.series ? chartObj.series : [];
  if (seriesArr.length === 0) return;
  var numSeries = seriesArr.length;
  var numCategories = Math.max.apply(null, seriesArr.map(function (s) { return s.data.length; }));
  if (numCategories === 0) return;

  var mmr = getMinMaxRange(seriesArr);
  var groupWidth = width / numCategories;
  var barWidth = (groupWidth * 0.8) / numSeries;
  var groupPad = groupWidth * 0.1;
  var depth = is3D ? Math.min(12, barWidth * 0.4) : 0;

  drawGridlines(ctx, x, y, width, height, mmr.minValue, mmr.range);

  seriesArr.forEach(function (series, sIdx) {
    series.data.forEach(function (value, cIdx) {
      var barHeight = ((value - mmr.minValue) / mmr.range) * height;
      var barX = x + cIdx * groupWidth + groupPad + sIdx * barWidth;
      var barY = y + height - barHeight;
      var color = series.color || COLORS[sIdx % COLORS.length];

      if (is3D) {
        // Right face
        ctx.fillStyle = shadeColor(color, -25);
        ctx.beginPath();
        ctx.moveTo(barX + barWidth, barY);
        ctx.lineTo(barX + barWidth + depth, barY - depth);
        ctx.lineTo(barX + barWidth + depth, barY + barHeight - depth);
        ctx.lineTo(barX + barWidth, barY + barHeight);
        ctx.closePath();
        ctx.fill();
        // Top face
        ctx.fillStyle = shadeColor(color, 15);
        ctx.beginPath();
        ctx.moveTo(barX, barY);
        ctx.lineTo(barX + depth, barY - depth);
        ctx.lineTo(barX + barWidth + depth, barY - depth);
        ctx.lineTo(barX + barWidth, barY);
        ctx.closePath();
        ctx.fill();
      }

      ctx.fillStyle = color;
      ctx.fillRect(barX, barY, barWidth, barHeight);
    });
  });

  var band = drawCategoryLabels(ctx, x, y, height, chartObj.categories || [], numCategories, groupWidth, chartObj.catLevels);
  drawAxes(ctx, x, y, width, height);
  drawLegend(ctx, x, y, width, height, seriesArr, { labelBand: band });
}

export function drawStackedColumnChart(ctx, x, y, width, height, chartObj, isPercent, is3D) {
  var seriesArr = chartObj && chartObj.series ? chartObj.series : [];
  if (seriesArr.length === 0) return;
  var numCategories = Math.max.apply(null, seriesArr.map(function (s) { return s.data.length; }));
  if (numCategories === 0) return;

  // Compute per-category totals
  var totals = new Array(numCategories).fill(0);
  for (var c = 0; c < numCategories; c++) {
    seriesArr.forEach(function (s) {
      totals[c] += s.data[c] || 0;
    });
  }
  var maxTotal = isPercent ? 1 : Math.max.apply(null, totals.concat([1]));
  var minValue = 0;
  var range = maxTotal - minValue || 1;

  // Y axis labels
  ctx.strokeStyle = '#E0E0E0';
  ctx.lineWidth = 1;
  for (var i = 0; i <= 5; i++) {
    var gy = y + height - (i / 5) * height;
    ctx.beginPath();
    ctx.moveTo(x, gy);
    ctx.lineTo(x + width, gy);
    ctx.stroke();
    var val = minValue + (i / 5) * range;
    ctx.fillStyle = '#888';
    ctx.font = '9px Arial';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(isPercent ? Math.round(val * 100) + '%' : (val % 1 === 0 ? val.toString() : val.toFixed(1)), x - 4, gy);
  }

  var groupWidth = width / numCategories;
  var barWidth = groupWidth * 0.7;
  var groupPad = (groupWidth - barWidth) / 2;
  var depth = is3D ? Math.min(12, barWidth * 0.25) : 0;

  for (var ci = 0; ci < numCategories; ci++) {
    var runningSum = 0;
    var categoryTotal = totals[ci] || 1;
    seriesArr.forEach(function (series, sIdx) {
      var v = series.data[ci] || 0;
      var displayVal = isPercent ? (v / (categoryTotal || 1)) : v;
      var barH = (displayVal / range) * height;
      var barY = y + height - ((runningSum + displayVal) / range) * height;
      var barX = x + ci * groupWidth + groupPad;
      var color = series.color || COLORS[sIdx % COLORS.length];

      if (is3D) {
        ctx.fillStyle = shadeColor(color, -25);
        ctx.beginPath();
        ctx.moveTo(barX + barWidth, barY);
        ctx.lineTo(barX + barWidth + depth, barY - depth);
        ctx.lineTo(barX + barWidth + depth, barY + barH - depth);
        ctx.lineTo(barX + barWidth, barY + barH);
        ctx.closePath();
        ctx.fill();
        if (sIdx === seriesArr.length - 1) {
          ctx.fillStyle = shadeColor(color, 15);
          ctx.beginPath();
          ctx.moveTo(barX, barY);
          ctx.lineTo(barX + depth, barY - depth);
          ctx.lineTo(barX + barWidth + depth, barY - depth);
          ctx.lineTo(barX + barWidth, barY);
          ctx.closePath();
          ctx.fill();
        }
      }

      ctx.fillStyle = color;
      ctx.fillRect(barX, barY, barWidth, barH);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 0.5;
      ctx.strokeRect(barX, barY, barWidth, barH);

      runningSum += displayVal;
    });
  }

  var band = drawCategoryLabels(ctx, x, y, height, chartObj.categories || [], numCategories, groupWidth, chartObj.catLevels);
  drawAxes(ctx, x, y, width, height);
  drawLegend(ctx, x, y, width, height, seriesArr, { labelBand: band });
}
